import { API_BASE_URL, apiRequest, apiRequestBlob, type ApiRequestOptions } from '../http.ts';
import type { APIResponse } from './types/common.ts';
import type {
  LogFileContentData,
  LogFilesData,
  LogsResponse,
  SecurityEventFilters,
  SecurityEventsData,
  SecurityAlertSettings,
  SecurityAlertSettingsStatus,
  TradeLogsData,
} from './types/logs.ts';
import type { DebugLoggingSettingsData, LoggingSettingsData } from './types/system.ts';

export interface TradeLogFilters {
  symbol?: string;
  strategy?: string;
  startDate?: string;
  endDate?: string;
  decisionIds?: string[];
}

function securityEventQuery(filters: SecurityEventFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.startAt) params.set('start_at', filters.startAt);
  if (filters.endAt) params.set('end_at', filters.endAt);
  if (filters.eventType) params.set('event_type', filters.eventType);
  if (filters.outcome) params.set('outcome', filters.outcome);
  if (filters.sourceIp) params.set('source_ip', filters.sourceIp);
  if (filters.actorUserId) params.set('actor_user_id', String(filters.actorUserId));
  if (filters.subjectUserId) params.set('subject_user_id', String(filters.subjectUserId));
  return params;
}

/** Trading history, runtime logs, and administrator log-management endpoints. */
export class LogsApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  getTradeLogs(
    page: number = 1,
    pageSize: number = 50,
    filters: TradeLogFilters = {},
  ): Promise<APIResponse<TradeLogsData>> {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (filters.symbol) params.set('symbol', filters.symbol);
    if (filters.strategy) params.set('strategy', filters.strategy);
    if (filters.startDate) params.set('start_date', filters.startDate);
    if (filters.endDate) params.set('end_date', filters.endDate);
    filters.decisionIds?.forEach((decisionId) => params.append('decision_ids', decisionId));
    return this.request<APIResponse<TradeLogsData>>(
      `${this.baseUrl}/trades/logs?${params.toString()}`,
    );
  }

  getRecentLogs(maxLines: number = 100, levelFilter?: string, logFile: string = 'all'): Promise<LogsResponse> {
    const params = new URLSearchParams({ max_lines: String(maxLines), log_file: logFile });
    if (levelFilter) params.set('level_filter', levelFilter);
    return this.request<LogsResponse>(`${this.baseUrl}/logs/recent?${params.toString()}`);
  }

  listLogFiles(): Promise<APIResponse<LogFilesData>> {
    return this.request<APIResponse<LogFilesData>>(`${this.baseUrl}/admin/logs/files`);
  }

  getSecurityEvents(
    filters: SecurityEventFilters = {},
  ): Promise<APIResponse<SecurityEventsData>> {
    const params = new URLSearchParams({
      page: String(filters.page ?? 1),
      page_size: String(filters.pageSize ?? 50),
    });
    securityEventQuery(filters).forEach((value, key) => params.set(key, value));
    return this.request<APIResponse<SecurityEventsData>>(
      `${this.baseUrl}/admin/security-events?${params.toString()}`,
    );
  }

  exportSecurityEvents(filters: SecurityEventFilters = {}): Promise<Blob> {
    const params = securityEventQuery(filters);
    params.set('format', 'csv');
    return apiRequestBlob(
      `${this.baseUrl}/admin/security-events?${params.toString()}`,
    );
  }

  getSecurityAlertSettings(): Promise<APIResponse<SecurityAlertSettingsStatus>> {
    return this.request(`${this.baseUrl}/admin/security-alert-settings`);
  }

  updateSecurityAlertSettings(settings: SecurityAlertSettings): Promise<APIResponse<SecurityAlertSettings>> {
    return this.request(`${this.baseUrl}/admin/security-alert-settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(settings),
    });
  }

  getLoggingSettings(): Promise<APIResponse<LoggingSettingsData>> {
    return this.request<APIResponse<LoggingSettingsData>>(
      `${this.baseUrl}/admin/settings/logging`,
    );
  }

  setDebugLogging(enabled: boolean): Promise<APIResponse<DebugLoggingSettingsData>> {
    return this.request<APIResponse<DebugLoggingSettingsData>>(
      `${this.baseUrl}/admin/settings/logging/debug`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
      },
    );
  }

  getLogFileContent(
    filename: string,
    page: number = 1,
    pageSize: number = 100,
    level?: string,
    search?: string,
    startDate?: string,
    endDate?: string,
  ): Promise<APIResponse<LogFileContentData>> {
    const params = new URLSearchParams({ filename, page: String(page), page_size: String(pageSize) });
    if (level && level !== 'ALL') params.set('level', level);
    if (search) params.set('search', search);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    return this.request<APIResponse<LogFileContentData>>(
      `${this.baseUrl}/admin/logs/content?${params.toString()}`,
    );
  }

  exportLogFile(filename: string): Promise<Blob> {
    const params = new URLSearchParams({ filename });
    return apiRequestBlob(`${this.baseUrl}/admin/logs/export?${params.toString()}`);
  }
}
